import { cn } from '@/lib/utils';
import { useLocalization } from '@/hooks/useLocalization';
import { useUserRole } from '@/hooks/useUserRole';
import { useAttention } from '@/hooks/useAttention';
import { sectionForView, visibleViews, VIEW_LABELS } from '@/lib/navigation';
import { homeFor } from '@/lib/roleHome';

interface SectionTabsProps {
  activeView: string;
  onViewChange: (view: string) => void;
}

/**
 * One linear row of tabs for the views in the active section. Every tab is just
 * a view key (?view=), so RBAC, deep links and ProtectedFeature are unchanged.
 * Hidden when the role can open only one view in the section.
 */
const SectionTabs = ({ activeView, onViewChange }: SectionTabsProps) => {
  const { role, loading: roleLoading } = useUserRole();
  const attention = useAttention();
  const { t } = useLocalization();
  const section = sectionForView(activeView);
  if (!section) return null;
  const views = visibleViews(section, role);
  const showTabs = views.length > 1;
  // The default view is the role's own home screen: its label and question replace the section's, so there is
  // exactly one heading on the page (home screens do not print their own).
  const heading = activeView === 'dashboard' ? homeFor(role) : section;
  // The home heading depends on the role; hold its space until the role is known instead of flashing Today's.
  const headingPending = activeView === 'dashboard' && roleLoading;

  return (
    <div className="mb-6">
      <p className={cn('font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground', headingPending && 'invisible')}>{t(heading.label)}</p>
      <h1 className={cn('mt-1 font-heading text-2xl font-bold tracking-tight text-foreground', headingPending && 'invisible')} aria-busy={headingPending || undefined}>{t(heading.question)}</h1>
      {showTabs && (
        <div role="tablist" aria-label={t(section.label)} className="mt-4 flex gap-1 overflow-x-auto border-b border-border">
          {views.map((v) => {
            const active = v === activeView;
            return (
              <button
                key={v}
                role="tab"
                type="button"
                aria-selected={active}
                data-view-tab={v}
                onClick={() => onViewChange(v)}
                className={cn(
                  '-mb-px whitespace-nowrap border-b-2 px-4 py-2.5 font-heading text-sm font-semibold transition-colors',
                  active
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                {t(VIEW_LABELS[v] ?? v)}
                {(attention.byView[v] ?? 0) > 0 && (
                  <span
                    className={cn(
                      'ms-2 inline-block min-w-5 rounded-full px-1.5 text-center font-mono text-[11px] tabular-nums',
                      v === 'risks' && attention.criticalRisks > 0 || v === 'signals' && attention.criticalSignals > 0
                        ? 'bg-destructive text-destructive-foreground'
                        : 'bg-muted text-muted-foreground',
                    )}
                    aria-label={`${attention.byView[v]} ${v === 'decisions' || v === 'decideBoard' || v === 'hitlWorkbench' ? 'waiting' : 'open'}`}
                  >
                    {attention.byView[v]}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SectionTabs;
